"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, House as Home, Trees, ClipboardCheck, ShieldCheck, HardHat, FileCheck2, ArrowRight, CircleCheck as CheckCircle2, MapPin } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const accessEnvironments = [{
  icon: Building2,
  title: "Town-centre shopfronts",
  tag: "High-footfall access",
  description: "Carefully planned pedestrian tunnels, debris netting, and timed out-of-hours deliveries keep busy high streets and commercial frontages moving safely.",
  points: ["Pedestrian gantry & protective fans", "Public highway licensing assistance", "Out-of-hours logistics coordination"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/town_centre_scaffold_access.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/town_centre_scaffold_access.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/town_centre_scaffold_access.png-wvc-sizes",
  alt: "Town centre shopfront scaffolding with pedestrian protection"
}, {
  icon: Home,
  title: "Residential cul-de-sacs",
  tag: "Tight residential turns",
  description: "Compact fleet vehicles and respectful loading procedures ensure clear driveways, preserved verges, and minimal disruption for neighbours.",
  points: ["Manoeuvrable vehicle access", "Driveway and conservatory protection", "Considerate neighbour liaison"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/residential_culdesac_scaffold.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/residential_culdesac_scaffold.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/residential_culdesac_scaffold.png-wvc-sizes",
  alt: "Residential cul-de-sac scaffolding setup with clear access"
}, {
  icon: Trees,
  title: "Semi-rural surroundings",
  tag: "Challenging terrain",
  description: "Practical solutions for unpaved tracks, mature landscaping, soft ground, and uneven boundary gradients across South East properties.",
  points: ["Ground protection and sole pads", "Long-reach carry access options", "Heritage and landscape preservation"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/rural_property_scaffold.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/rural_property_scaffold.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/rural_property_scaffold.png-wvc-sizes",
  alt: "Semi rural scaffolding setup on uneven terrain"
}];
const safetySteps = [{
  step: "01",
  title: "Site assessment",
  description: "We survey ground conditions, structural tie points, overhead hazards, and public boundaries before any equipment arrives on site.",
  icon: HardHat
}, {
  step: "02",
  title: "Safe access installation",
  description: "Erected to strict TG20 and SG4 standards with integrated ladder gates, edge protection, and secure harness anchor points.",
  icon: ShieldCheck
}, {
  step: "03",
  title: "Handover inspection",
  description: "Every structure is formally signed off with an official handover certificate and Scafftag before any trade steps onto the boards.",
  icon: ClipboardCheck
}];
const nearbyAreas = [{
  name: "Farnborough",
  path: "/scaffolding-in-farnborough",
  detail: "Commercial and residential scaffolding"
}, {
  name: "Basingstoke",
  path: "/scaffolding-in-basingstoke",
  detail: "Development, roofing and industrial access"
}, {
  name: "Aldershot",
  path: "/scaffolding-in-aldershot",
  detail: "Town-centre and domestic scaffold projects"
}];
const photoNameMap: Record<string, string> = {
  "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/town_centre_scaffold_access.png": "Fleet Town Centre Scaffolding Image",
  "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/residential_culdesac_scaffold.png": "Fleet Residential Cul-de-Sac Scaffolding Image",
  "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/rural_property_scaffold.png": "Fleet Rural Property Scaffolding Image"
};
function AccessSafetyCards() {
  const {
    posts,
    loading
  } = usePosts();
  const getResolvedImage = (env: (typeof accessEnvironments)[number]) => {
    const targetName = photoNameMap[env.image];
    if (!targetName) {
      return {
        src: env.image,
        srcset: env.srcset,
        sizes: env.sizes,
        alt: env.alt
      };
    }
    const matchedPost = posts?.find(p => p.title?.trim().toLowerCase() === targetName.toLowerCase() || p.customFields?.photo_name?.trim().toLowerCase() === targetName.toLowerCase());
    const postImg = matchedPost?.customFields?.image || matchedPost?.featuredImage;
    const postAlt = matchedPost?.customFields?.alt_text || matchedPost?.title;
    if (postImg) {
      return {
        src: postImg,
        srcset: "",
        sizes: "",
        alt: postAlt || env.alt
      };
    }
    return {
      src: env.image,
      srcset: env.srcset,
      sizes: env.sizes,
      alt: env.alt
    };
  };
  return <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
      {accessEnvironments.map((env, i) => {
      const IconComponent = env.icon;
      const resolved = getResolvedImage(env);
      return <div key={env.title} data-index={i} className="flex">
            <Card className="flex flex-col justify-between overflow-hidden border border-border bg-card shadow-sm transition-[box-shadow,border-color] duration-200 hover:border-primary/40 hover:shadow-md w-full">
              <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
                {loading ? <div className="h-full w-full bg-muted animate-pulse" /> : <img src={resolved.src} data-wvc-srcset={resolved.srcset} data-wvc-sizes={resolved.sizes} alt={resolved.alt} loading="lazy" decoding="async" className="h-full w-full object-cover" />}
                <div className="absolute top-3 left-3">
                  <span className="rounded-md bg-secondary/90 px-2.5 py-1 text-xs font-medium text-secondary-foreground backdrop-blur-xs">
                    {env.tag}
                  </span>
                </div>
              </div>

              <CardHeader className="pb-3">
                <div className="mb-2 inline-flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <IconComponent className="size-5" />
                </div>
                <CardTitle className="text-xl font-semibold text-card-foreground">
                  {env.title}
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground pt-1">
                  {env.description}
                </CardDescription>
              </CardHeader>

              <CardContent className="pt-0">
                <Separator className="my-3" />
                <ul className="space-y-2 text-sm text-foreground">
                  {env.points.map((point, pointIndex) => <li key={pointIndex} data-index={pointIndex} className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
                      <span className="text-muted-foreground">{point}</span>
                    </li>)}
                </ul>
              </CardContent>
            </Card>
          </div>;
    })}
    </div>;
}
export default function AccessSafety() {
  const shouldReduceMotion = useReducedMotion();
  const fadeVariant = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="fleetaccessandsafety" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeVariant} className="mx-auto max-w-3xl text-center">
          <Badge variant="outline" className="mb-4">
            Fleet access & site safety
          </Badge>
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl text-foreground">
            Built for tight streets, open plots, and uncompromising safety
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground">
            Serving projects throughout Fleet across the GU51 and GU52 postcode districts, we deliver practical access, considerate logistics, and verified safety on every setup.
          </p>
        </motion.div>

        {/* Fleet Access Environments Grid via PostsProvider */}
        <PostsProvider wp_query={{
        post_type: "site_photo",
        per_page: 50,
        status: ["publish"]
      }}>
          <AccessSafetyCards />
        </PostsProvider>

        {/* Safety Workflow and Accreditations Banner */}
        <div className="mt-16 sm:mt-20">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.15
        }} variants={fadeVariant} className="rounded-2xl border border-border bg-card p-6 sm:p-10 lg:p-12 shadow-sm">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 pb-8 border-b border-border">
              <div className="max-w-2xl">
                <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                  Three-stage verification
                </span>
                <h3 className="mt-1 text-2xl font-bold text-card-foreground sm:text-3xl">
                  Safety verified at every stage of the build
                </h3>
                <p className="mt-2 text-sm sm:text-base text-muted-foreground">
                  Our qualified scaffolders follow a strict assessment and
                  sign-off regime, protecting your trades, your property, and
                  the public.
                </p>
              </div>

              {/* Accreditations highlight - NASC and CHAS only */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/50 px-4 py-3">
                  <FileCheck2 className="size-6 text-primary" />
                  <div>
                    <div className="text-sm font-bold text-foreground">
                      NASC
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Audited safety standards
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/50 px-4 py-3">
                  <ShieldCheck className="size-6 text-primary" />
                  <div>
                    <div className="text-sm font-bold text-foreground">
                      CHAS
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Accredited contractor
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 3 Step Safety Protocol */}
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
              {safetySteps.map((stepItem, i) => {
              const StepIcon = stepItem.icon;
              return <div key={stepItem.step} data-index={i} className="relative rounded-xl border border-border/80 bg-background/60 p-5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-primary">
                        {stepItem.step}
                      </span>
                      <StepIcon className="size-5 text-muted-foreground" />
                    </div>
                    <h4 className="mt-3 text-base font-semibold text-foreground">
                      {stepItem.title}
                    </h4>
                    <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                      {stepItem.description}
                    </p>
                  </div>;
            })}
            </div>

            {/* Call to action footer bar */}
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-xl bg-muted/40 p-4 sm:p-6">
              <div className="text-center sm:text-left">
                <h4 className="text-sm font-semibold text-foreground">
                  Need a detailed site survey or access plan?
                </h4>
                <p className="text-xs text-muted-foreground">
                  Our team assesses your access constraints and provides fixed
                  quotes.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Button asChild variant="outline" size="sm">
                  <Link to="/HealthSafetyAccreditationsPage">
                    View accreditations
                  </Link>
                </Button>
                <Button asChild size="sm">
                  <Link to="/ContactRequestQuotePage">
                    Request site visit
                    <ArrowRight className="size-3.5" />
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Nearby Areas Coverage Section */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} variants={fadeVariant} className="mt-12 sm:mt-16 rounded-2xl border border-border bg-card p-6 sm:p-8 lg:p-10 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-6 border-b border-border">
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                <MapPin className="size-4" />
                <span>Regional Coverage</span>
              </div>
              <h3 className="mt-1 text-2xl font-bold text-card-foreground">
                Fleet (GU51 & GU52) and nearby coverage
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                In addition to Fleet postcode districts GU51 and GU52, we regularly supply scaffolding across key neighbouring areas.
              </p>
            </div>
            <Button asChild variant="outline" size="sm" className="self-start md:self-auto shrink-0">
              <Link to="#">
                View all areas
                <ArrowRight className="size-3.5" />
              </Link>
            </Button>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {nearbyAreas.map((area, i) => <Link key={area.name} to={area.path} data-index={i} className="group flex flex-col justify-between rounded-xl border border-border bg-background p-5 transition-[border-color,box-shadow] hover:border-primary/50 hover:shadow-sm">
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-base font-semibold text-foreground group-hover:text-primary transition-colors">
                      {area.name}
                    </span>
                    <ArrowRight className="size-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {area.detail}
                  </p>
                </div>
                <div className="mt-4 text-xs font-medium text-primary">
                  Scaffolding in {area.name}
                </div>
              </Link>)}
          </div>
        </motion.div>
      </div>
    </section>;
}